import { Component, Input, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { AddEvent, EditEvent, SaveEvent, GridComponent } from '@progress/kendo-angular-grid';
import { CancelEvent, RemoveEvent } from '@progress/kendo-angular-upload';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { takeUntil, debounceTime, Observable, Subject, distinctUntilChanged, map, filter } from 'rxjs';
import { ORD_Powder, PatchORDBitDto, PatchORDOrderDto, PatchORDPowderDto, RTAOptions } from '../../interfaces/request.interface';
import { GlobalNotificationService } from '../../services/globalnotification.service';
import { RequestActions } from '../../store/action/request.action';
import { OptionState } from '../../store/state/options.state';
import { RequestState } from '../../store/state/request.state';

@Component({
    selector: 'app-powderbinder-data',
    templateUrl: './powderbinder-data.component.html',
    styleUrl: './powderbinder-data.component.css',
    standalone: false
})
export class PowderbinderDataComponent {
  @Input() powderInitial?: ORD_Powder[];
  @Input() draftId!: number;
  @Input() Size!: number;
  @Input() readonly: boolean = false;


  public appPowderData!: FormGroup;
  public rtaOptions$!: Observable<RTAOptions[]>;
  private destroy$ = new Subject<void>();

  public uppersections_Data$!: Observable<any[]>;
  public electronics_Data$!: Observable<any[]>;


  public FacePowder_Data: any[] = [];
  public GaugePowder_Data: any[] = [];
  public MachinablePowder_Data: any[] = [];
  public Binder_Data: any[] = [];
  public store_PowderBinderData$!: Observable<any[]>;
  public store_StandardPowderData$!: Observable<any[]>;

  public StandardPowder_Data: any[] = [];
  public PowderBinder_Data: any[] = [];

  constructor(private fb: FormBuilder, private store: Store, private route: ActivatedRoute,
    private globalNotificationService: GlobalNotificationService) {
    this.appPowderData = this.fb.group({
      FACE: [null, Validators.required],
      GAUGE: [null, Validators.required],
      MACHINABLE: [null, Validators.required],
      BINDER: [null, Validators.required],
      standardPowderSet: [null]
    });
  }
  // FormGroup options
  public PowderBinder: any[] = [];

  ngOnInit(): void {
    this.store_PowderBinderData$ = this.store.select(OptionState.bodyMaterial);
    this.store_StandardPowderData$ = this.store.select(OptionState.PowderStandards);

    this.store_PowderBinderData$
    .pipe(takeUntil(this.destroy$))
    .subscribe(data => this.PowderBinder_Data = data);

  this.store_StandardPowderData$
    .pipe(takeUntil(this.destroy$))
    .subscribe(data => this.StandardPowder_Data = data);

    // Subscribe to standardPowderSet changes
    this.appPowderData.get('standardPowderSet')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter(value => value != null)
      )
      .subscribe(selectedSetName => {
        this.applyStandardPowderSet(selectedSetName);
      });

    this.appPowderData.valueChanges.pipe(
      debounceTime(200),
      takeUntil(this.destroy$)
    ).subscribe(vals => {
      const updated: any[] = [];
      for (const loc of ['FACE', 'GAUGE', 'MACHINABLE', 'BINDER']) {
        updated.push({
          id: this.powderInitial?.find(p => p.location === loc)?.id,
          type: vals[loc],
          location: loc,
          orderId: this.draftId
        });
      }
      this.store.dispatch(new RequestActions.PatchRequestDraft({
        draftId: this.draftId,
        changes: { powders: updated }
      }));
    });
    this.applyFilters();
  }
  ngOnChanges(changes: SimpleChanges): void {
    // Handle readonly state changes
    if (changes['readonly']) {
      if (this.readonly) {
        this.appPowderData?.disable();
      } else {
        this.appPowderData?.enable();
      }
    }
    
    const ch = changes['powderInitial'];
    if (ch && ch.currentValue) {
      const arr: PatchORDPowderDto[] = ch.currentValue;
      this.appPowderData.patchValue({
        FACE: this.findType(arr, 'FACE'),
        GAUGE: this.findType(arr, 'GAUGE'),
        MACHINABLE: this.findType(arr, 'MACHINABLE'),
        BINDER: this.findType(arr, 'BINDER')
      });
      
      // Re-apply readonly state after patching values
      if (this.readonly) {
        this.appPowderData?.disable();
      }
    }
    if (changes['Size'] && !changes['Size'].firstChange) {
      this.applyFilters();
    }
  }
  private findType(arr: PatchORDPowderDto[], location: string): string | undefined {
    return arr.find(p => p.location === location)?.type;
  }
  applyFilters() {
    const FacePowder: any[] = [];
    const GaugePowder: any[] = [];
    const MachinablePowder: any[] = [];
    const Binder: any[] = [];
    this.PowderBinder_Data.filter((p) => p['BitMin'] <= (this.Size ?? 0) && p['BitMax'] >= (this.Size ?? 0)).forEach((data: any) => {
      switch (data.Location) {
        case 'BOTH':
          FacePowder.push({
            value: data.value,
            name: data.name,
            BitMin: data.BitMin,
            BitMax: data.BitMax
          });
          GaugePowder.push({
            value: data.value,
            name: data.name,
            BitMin: data.BitMin,
            BitMax: data.BitMax
          });
          break;
        case 'FACE':
          FacePowder.push({
            value: data.value,
            name: data.name,
            BitMin: data.BitMin,
            BitMax: data.BitMax
          });
          break;
        case 'BODY':
          GaugePowder.push({
            value: data.value,
            name: data.name,
            BitMin: data.BitMin,
            BitMax: data.BitMax
          });
          break;
        case 'MACHINABLE':
          MachinablePowder.push({
            value: data.value,
            name: data.name,
            BitMin: data.BitMin,
            BitMax: data.BitMax
          });
          break;
        case 'BINDER':
          Binder.push({
            value: data.value,
            name: data.name,
            BitMin: data.BitMin,
            BitMax: data.BitMax
          });
          break;
      }
    });
    this.FacePowder_Data = FacePowder;
    this.GaugePowder_Data = GaugePowder;
    this.MachinablePowder_Data = MachinablePowder;
    this.Binder_Data = Binder;
  }
  getStandardPowderSets(){
    return this.StandardPowder_Data.filter((p) => p['SizeMin'] <= (this.Size ?? 0) && p['SizeMax'] >= (this.Size ?? 0));
  }
  
  applyStandardPowderSet(setName: any): void {
      // Update the form fields with values from the selected set
      this.appPowderData.patchValue({
        FACE: setName.face,
        GAUGE: setName.gauge,
        MACHINABLE: setName.machinable,
        BINDER: setName.binder
      }, { emitEvent: true });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
